import { useState, useEffect, useCallback } from "react";
import { Button, Space, Select, Input, DatePicker, Tag, message } from "antd";
import { ReloadOutlined, CheckOutlined } from "@ant-design/icons";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { INSTALLMENT_STATUS_OPTIONS, formatOmr } from "./constants";
import {
  listEmiInstallments, getEmiInstallmentKpis, processEmiInstallment,
  exportEmiInstallments,
} from "../../services/payroll.service";

const { RangePicker } = DatePicker;

/**
 * EMI KPI Summary — the generated instalment ledger.
 *
 * Rows are materialised monthly by the backend's EMI scheduler; this tab is
 * where an admin marks one processed, which posts it as a rider expense.
 */
export default function EMIKpiTab() {
  const { canEdit } = usePermission("payrollExpenses");
  const { options: workTypeOptions } = useSettingsDropdown("work-types");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState();
  const [workType, setWorkType] = useState();
  const [dateRange, setDateRange] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      status: status || undefined,
      workType: workType || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, status, workType, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listEmiInstallments(params({ page, per_page: pageSize })),
        getEmiInstallmentKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load EMI instalments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, status, workType, dateRange]);

  const handleProcess = async (record) => {
    setProcessingId(record._id);
    try {
      await processEmiInstallment(record._id);
      message.success("Instalment processed and posted as a rider expense");
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not process instalment");
    } finally {
      setProcessingId(null);
    }
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "EMI Month", dataIndex: "emiMonthLabel" },
    { title: "Expense Type", dataIndex: "expenseTypeName" },
    { title: "Work Type", dataIndex: "workTypeName" },
    { title: "Instalment No", dataIndex: "installmentNo", align: "right" },
    {
      title: "Amount (OMR)",
      dataIndex: "installmentAmount",
      align: "right",
      render: formatOmr,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value, r) => (
        <Tag color={value === "processed" ? "green" : "gold"}>{r.statusLabel}</Tag>
      ),
    },
    { title: "Processed By", dataIndex: "processedByName" },
    ...(canEdit
      ? [
          {
            title: "Action",
            key: "process",
            width: 130,
            render: (_, record) =>
              record.status === "pending" ? (
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  loading={processingId === record._id}
                  onClick={() => handleProcess(record)}
                >
                  Process
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* BRD §5.5: Total Active EMIs (count + outstanding), Completed this
          month / year, Total Deductions this month — plus the instalment
          counts this tab's table lists. */}
      <KPIGrid minWidth={170}>
        <KPICard label="Total Active EMIs" value={kpis.activeEmis} />
        <KPICard label="Active Outstanding" value={kpis.activeOutstanding} unit="OMR" tone="dark" />
        <KPICard label="Completed This Month" value={kpis.completedThisMonth} tone="positive" />
        <KPICard label="Completed This Year" value={kpis.completedThisYear} tone="positive" />
        <KPICard label="Deductions This Month" value={kpis.deductionsThisMonth} unit="OMR" />
        <KPICard label="All Instalments" value={kpis.all} />
        <KPICard label="Processed" value={kpis.processed} />
        <KPICard label="Pending" value={kpis.pending} />
        <KPICard label="Instalments Processed" value={kpis.processedAmount} unit="OMR" />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search partner"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 160 }}
          options={INSTALLMENT_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <Select
          placeholder="Work Type"
          allowClear
          style={{ width: 160 }}
          options={workTypeOptions}
          value={workType}
          onChange={setWorkType}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="EMI_KPI"
          onExport={() => exportEmiInstallments(params())}
        />
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
      />
    </div>
  );
}
